import { useLocation, useNavigate } from "react-router-dom";
import * as yup from "yup";
import { useAuth } from "../../context/AuthContext";
import { useCallback, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { ApolloError, useLazyQuery, useMutation } from "@apollo/client";
import { EMPLOYEES_BY_AGENCY_ID, ENHANCEMENT_BY_ID } from "../../api/query";
import { CREATE_ENHANCEMENT, ADD_ENHANCEMENT_HISTORY } from "../../api/mutation";
import {
  showError,
  showSuccess,
} from "../../components/common/ToastNotification";
import BreadCumb from "../../components/common/Breadcrumb";
import SearchableInputsDropdown from "../../components/common/SearchableDropdown";
import InputsNew from "../../components/common/InputsNew";
import backArrow from "../../assets/task/back_arrow.svg";
import Loader from "../../components/common/Loader";
import { format } from "date-fns";
import { getSupabaseExt } from "../../lib/supabase";
import DateSelector from "../../components/common/DateSelector";

const platformOptions = [
  { key: "Web", name: "Web" },
  { key: "Mobile", name: "Mobile" },
  { key: "API", name: "API" },
  { key: "Other", name: "Other" },
];

const priorityOptions = [
  { key: "Low", name: "Low" },
  { key: "Medium", name: "Medium" },
  { key: "High", name: "High" },
  { key: "Critical", name: "Critical" },
];

const enhancementStatusOptions = [
  { key: "Open", name: "Open" },
  { key: "In Progress", name: "In Progress" },
  { key: "Resolved", name: "Resolved" },
  { key: "Closed", name: "Closed" },
];



// ── Schemas ─────────────────────────────────────────────────────
const editEnhancementSchema = yup.object().shape({
  enhancement_reported_on: yup.string().required("This field is required"),
  reported_by_name: yup.string().required("This field is required"),
  reported_by_id: yup.string().required("This field is required"),
  agency_name: yup
    .string()
    .test(
      "ws",
      "Please add a valid Agency Name",
      (v) => !v || v.trim().length > 0,
    )
    .required("This field is required"),
  platform: yup.string().required("This field is required"),
  other_platform: yup.string().when("platform", {
    is: "Other",
    then: (schema) =>
      schema
        .trim()
        .required("Please enter Platform")
        .test(
          "not-only-whitespace",
          "Please enter valid Platform",
          (value) => !!value?.trim(),
        ),
    otherwise: (schema) => schema.notRequired(),
  }),
  priority: yup.string().required("This field is required"),
  enhancement_details: yup
    .string()
    .test(
      "ws",
      "Please add valid Enhancement Details",
      (v) => !v || v.trim().length > 0,
    )
    .required("This field is required"),
  enhancement_assigned_name: yup.string().required("This field is required"),
  enhance_assigned_id: yup.string().required("This field is required"),
  enhancement_status: yup.string().required("This field is required"),
  release_date: yup.string(),
    work_started_on:yup.string(),
  remarks: yup.string(),
});

const addHistorySchema = yup.object().shape({
  changes_made: yup
    .string()
    .test("ws", "Please add valid changes", (v) => !v || v.trim().length > 0)
    .required("This field is required"),
  worked_on: yup.string().required("This field is required"),
});

// ────────────────────────────────────────────────────────────────
const ViewEnhancement = () => {
  const navigate = useNavigate();
  const { userInfo } = useAuth();
  const employeeID = userInfo?.employee_id || "";
  const organizationID = userInfo?.agencies?.id || "";

  const location = useLocation();
  const { enhanementID }: any = location.state as { enhanementID: number };

  const [employeeOptions, setEmployeeOptions] = useState<
    { key: string; name: string }[]
  >([]);
  const [historyList, setHistoryList] = useState<any[]>([]);
  const [enhancementSummary, setEnhancementSummary] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<"details" | "history">("details");
  const [extAgenciesLoading, setExtAgenciesLoading] = useState(false);
  const [extAgencies, setExtAgencies] = useState<any>([]);

  // ── Edit form ────────────────────────────────────────────────
  const {
    register: editRegister,
    handleSubmit: handleEditSubmit,
    setValue: editSetValue,
    formState: { errors: editErrors },
    trigger: editTrigger,
    watch: editWatch,
    clearErrors,
    control,
  } = useForm<any>({ resolver: yupResolver(editEnhancementSchema) });

  console.log(editErrors,'editErrors')

  // ── History form ─────────────────────────────────────────────
  const {
    register: histRegister,
    handleSubmit: handleHistSubmit,
    reset: histReset,
    formState: { errors: histErrors },
    control:histControl
  } = useForm<any>({ resolver: yupResolver(addHistorySchema) });

  // Fetch active agencies from external Supabase when meeting type is Client Meeting
  const fetchExtAgencies = useCallback(async () => {
    setExtAgenciesLoading(true);
    try {
      const { data, error } = await getSupabaseExt()
        .from("companies")
        .select("agency_id, name, status")
        .eq("status", "Active");
      if (!error && data) {
        setExtAgencies(
          data.map((row: any) => ({
            id: String(row.agency_id),
            name: row.name,
          })),
        );
      }
    } finally {
      setExtAgenciesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchExtAgencies();
  }, []);

  // ── Mutations ────────────────────────────────────────────────
  const [updateEnhance, { loading: updateEnhanceLoading }] = useMutation(CREATE_ENHANCEMENT, {
    fetchPolicy: "no-cache",
  });
  const [addenhanceHistory, { loading: addHistoryLoading }] = useMutation(
    ADD_ENHANCEMENT_HISTORY,
    { fetchPolicy: "no-cache" },
  );

  // ── Queries ──────────────────────────────────────────────────
  const [getEnhanceByID, { loading: enhanceLoading }] = useLazyQuery(ENHANCEMENT_BY_ID, {
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      const enhancement = data?.enhancement_by_id;
      if (!enhancement) return;

      // Populate edit form — IDs now come from the query
      editSetValue("enhancement_reported_on", enhancement.enhancement_reported_on || "");
      editSetValue("reported_by_name", enhancement.reported_by_name || "");
      editSetValue("reported_by_id", String(enhancement.reported_by ?? ""));
      editSetValue("agency_name", enhancement.agency_name || "");
  
      if(enhancement.platform == "Web" || enhancement.platform == "API" || enhancement.platform == "Mobile" ){
       editSetValue("platform", enhancement.platform || "");
      }else{
        editSetValue("platform", "Other");
      editSetValue("other_platform", enhancement.platform );
      }
      editSetValue("priority", enhancement.priority || "");
      editSetValue("enhancement_details", enhancement.enhancement_details || "");
      editSetValue("enhancement_assigned_name", enhancement.enhancement_assigned_name || "");
      editSetValue("enhance_assigned_id", String(enhancement.enhancement_assigned ?? ""));
      editSetValue("enhancement_status", enhancement.enhancement_status || "");
      editSetValue("release_date", enhancement.release_date || "");
      editSetValue("work_started_on", enhancement.work_started_on || "");

      editSetValue("remarks", enhancement.remarks || "");

      // Store summary for History tab context card
      setEnhancementSummary({
        platform: enhancement.platform,
        priority: enhancement.priority,
        enhancement_status: enhancement.enhancement_status,
        enhancement_details: enhancement.enhancement_details,
        reported_by_name: enhancement.reported_by_name,
        enhancement_assigned_name: enhancement.enhancement_assigned_name,
        enhancement_reported_on: enhancement.enhancement_reported_on,
        release_date: enhancement.release_date,
        work_started_on: enhancement.work_started_on,
        remarks: enhancement.remarks,
      });

      setHistoryList(enhancement.history || []);
    },
  });

  const [getEmployeesByAgencyID, { loading: employeesLoading }] = useLazyQuery(
    EMPLOYEES_BY_AGENCY_ID,
    {
      fetchPolicy: "no-cache",
      onCompleted: (data) => {
        if (data?.employees_by_agency_id) {
          setEmployeeOptions(
            data.employees_by_agency_id.employees.map((emp: any) => ({
              key: String(emp.employee_id),
              name: `${emp.first_name} ${emp.last_name}`,
            })),
          );
        }
      },
    },
  );

  // ── Init ─────────────────────────────────────────────────────
  useEffect(() => {
    if (enhanementID) getEnhanceByID({ variables: { enhancement_id: parseInt(enhanementID) } });
    if (organizationID)
      getEmployeesByAgencyID({
        variables: { agency_id: parseInt(organizationID) },
      });
  }, [enhanementID, organizationID]);

  // ── Watched values ───────────────────────────────────────────
  const watchReportedByName = editWatch("reported_by_name");
  const watchEnhancementAssignedName = editWatch("enhancement_assigned_name");
  const watchPlatform = editWatch("platform");
  const watchPriority = editWatch("priority");
  const watchEnhancementStatus = editWatch("enhancement_status");

  // ── Dropdown handlers ────────────────────────────────────────
  function handleSelectReportedBy(key: any, name: string) {
    editSetValue("reported_by_id", String(key));
    editSetValue("reported_by_name", name);
    editTrigger("reported_by_name");
  }
  function handleSelectEnhancementAssigned(key: any, name: string) {
    editSetValue("enhance_assigned_id", String(key));
    editSetValue("enhancement_assigned_name", name);
    editTrigger("enhancement_assigned_name");
  }

  function handleSelectPlatform(key: any, name: string) {
    editSetValue("platform", name);

    if (name === "Other") {
      editSetValue("other_platform", "");
      clearErrors("other_platform"); // Hide error until user interacts
    } else {
      editSetValue("other_platform", "");
      clearErrors("other_platform");
    }

    editTrigger("platform");
  }
  function handleSelectPriority(_key: any, name: string) {
    editSetValue("priority", name);
    editTrigger("priority");
  }
  function handleSelectEnhancementStatus(_key: any, name: string) {
    editSetValue("enhancement_status", name);
    editTrigger("enhancement_status");
  }

  // ── Submit: update enhancement ───────────────────────────────────────
  async function onEditSubmit(data: any) {
    const input: any = {
      id: parseInt(enhanementID),
      enhancement_reported_on: data.enhancement_reported_on,
      reported_by: parseInt(data.reported_by_id),
      agency_name: data.agency_name,
      platform: data.platform === "Other" ? data.other_platform : data.platform,
      priority: data.priority,
      enhancement_details: data.enhancement_details,
      enhancement_assigned: parseInt(data.enhance_assigned_id),
      enhancement_status: data.enhancement_status,
      release_date: data.release_date || null,
      work_started_on: data.work_started_on || null,
      remarks: data.remarks || "",
      created_by: parseInt(employeeID),
    };

    try {
      const response = await updateEnhance({ variables: { input } });
      const result = response.data?.create_enhancement;
      if (result?.status_code === 200 || result?.status_code === 201) {
        showSuccess("Enhancement updated successfully");
        navigate(-1);
      } else {
        showError("Failed to update enhancement");
      }
    } catch (error) {
      if (error instanceof ApolloError) showError(error.message);
    }
  }

  // ── Submit: add history entry ────────────────────────────────
  async function onHistorySubmit(data: any) {
    const input = {
      enhancement_id: parseInt(enhanementID),
      worked_by: parseInt(employeeID),
      changes_made: data.changes_made,
      worked_on: `${data.worked_on}T00:00:00`,
      created_by: parseInt(employeeID),
    };

    try {
      const response = await addenhanceHistory({ variables: { input } });
      const result = response.data?.add_enhancement_history;
      if (result?.status_code === 201 || result?.status_code === 200) {
        showSuccess("History entry added");
        histReset();
        getEnhanceByID({ variables: { enhancement_id: parseInt(enhanementID) } });
      } else {
        showError("Failed to add history entry");
      }
    } catch (error) {
      if (error instanceof ApolloError) showError(error.message);
    }
  }

  const isLoading =
    enhanceLoading || employeesLoading || updateEnhanceLoading || addHistoryLoading;

  return (
    <>
      <div
        id="cts-respon-p-dv"
        className="w-full relative overflow-hidden flex flex-row items-start justify-center flex-wrap content-start py-40 px-20 mq991:p-8 mq575:p-4 box-border text-left text-base text-gray font-manrope"
      >
        <div className="flex-1 flex flex-col items-start justify-start gap-6">
          <BreadCumb />

          {/* Page header */}
          <div className="self-stretch flex flex-row items-center justify-start gap-[5px] text-2xl">
            <img
              className="w-6 relative h-6 overflow-hidden shrink-0 cursor-pointer dark-white-img"
              alt=""
              src={backArrow}
              onClick={() => navigate(-1)}
            />
            <h3 className="m-0 flex-1 relative text-inherit font-bold font-inherit inline-block max-w-[calc(100%_-_29px)] mq450:text-base dark:text-white">
              Enhancement Details
            </h3>
          </div>

          {/* Tabs */}
          <div className="self-stretch flex flex-row items-center gap-[4px] border-b border-surface-border">
            <button
              type="button"
              onClick={() => setActiveTab("details")}
              className={`px-5 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors cursor-pointer ${
                activeTab === "details"
                  ? "border-surface-primary-surface text-surface-primary-surface"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              Edit Enhancement
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("history")}
              className={`px-5 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors cursor-pointer ${
                activeTab === "history"
                  ? "border-surface-primary-surface text-surface-primary-surface"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              History
              {historyList.length > 0 && (
                <span className="ml-2 bg-surface-primary-surface text-white text-xs rounded-full px-2 py-0.5">
                  {historyList.length}
                </span>
              )}
            </button>
          </div>

          {/* ── EDIT TAB ──────────────────────────────────────── */}
          {activeTab === "details" && (
            <form
              onSubmit={handleEditSubmit(onEditSubmit)}
              className="m-0 self-stretch rounded-radius-radius-lg bg-surface-bg-blue darkborder-b-remove flex flex-col items-start justify-start p-8 mq575:p-3 box-border gap-spacing-lg max-w-full mq825:pt-5 mq825:pb-5 mq825:box-border mq1425:pt-[21px] mq1425:pb-[21px] mq1425:box-border"
            >
              {/* Row 1 */}
              <div className="grid grid-cols-4 mq1400:grid-cols-4 mq1275:grid-cols-3 mq950:grid-cols-2 mq575:grid-cols-1 gap-[8px] min-w-full">
                <Controller
                  name="enhancement_reported_on"
                  control={control}
                  render={({ field }) => (
                    <DateSelector
                      label="Enhancement Reported Date*"
                      selectedDate={field.value}
                      onChange={(date: any) => {
                        field.onChange(date);
                      }}
                      nothideParsedValue={true}
                      blueIcon={true}
                    />
                  )}
                />
                <SearchableInputsDropdown
                  label="Platform*"
                  initialValue={watchPlatform}
                  placeHolder="Select Platform"
                  options={platformOptions}
                  onSelect={handleSelectPlatform}
                  errorMessage={editErrors?.platform}
                />
                {watchPlatform === "Other" && (
                  <InputsNew
                    type="text"
                    label="Platform Name*"
                    register={editRegister("other_platform")}
                    errorMessage={editErrors?.other_platform}
                    contentPlaceholder="Enter Platform Name"
                  />
                )}
                <SearchableInputsDropdown
                  label="Priority*"
                  initialValue={watchPriority}
                  placeHolder="Select Priority"
                  options={priorityOptions}
                  onSelect={handleSelectPriority}
                  errorMessage={editErrors?.priority}
                />
                <SearchableInputsDropdown
                  label="Enhancement Status*"
                  initialValue={watchEnhancementStatus}
                  placeHolder="Select Status"
                  options={enhancementStatusOptions}
                  onSelect={handleSelectEnhancementStatus}
                  errorMessage={editErrors?.enhancement_status}
                />
              </div>

              {/* Row 2 */}
              <div className="grid grid-cols-4 mq1400:grid-cols-4 mq1275:grid-cols-3 mq950:grid-cols-2 mq575:grid-cols-1 gap-[8px] min-w-full">
                <SearchableInputsDropdown
                  label="Reported By*"
                  initialValue={watchReportedByName}
                  placeHolder="Select Employee"
                  options={employeeOptions}
                  onSelect={handleSelectReportedBy}
                  errorMessage={editErrors?.reported_by_name}
                />
                <SearchableInputsDropdown
                  label="Enhancement Assigned*"
                  initialValue={watchEnhancementAssignedName}
                  placeHolder="Select Employee"
                  options={employeeOptions}
                  onSelect={handleSelectEnhancementAssigned}
                  errorMessage={editErrors?.enhancement_assigned_name}
                />

                <SearchableInputsDropdown
                  label="Agency Name*"
                  placeHolder="Agency Name*"
                  options={extAgencies}
                  onSelect={(id: any, name: any) => {
                    editSetValue("agency_name", name);
                    editTrigger("agency_name");
                  }}
                  errorMessage={editErrors?.agency_name}
                  initialValue={editWatch("agency_name")}
                />
                 <Controller
                name="work_started_on"
                control={control}
                render={({ field }) => (
                  <DateSelector
                    label="Work Started On"
                    selectedDate={field.value}
                    onChange={(date) => {
                      field.onChange(date);
                    }}
                    nothideParsedValue={true}
                    blueIcon={true}
                  />
                )}
              />

                <Controller
                  name="release_date"
                  control={control}
                  render={({ field }) => (
                    <DateSelector
                      label="Release Date*"
                      selectedDate={field.value}
                      onChange={(date: any) => {
                        field.onChange(date);
                      }}
                      nothideParsedValue={true}
                      blueIcon={true}
                    />
                  )}
                />
              </div>

              {/* Enhancement Details */}
              <div className="self-stretch flex flex-col items-start justify-start text-sm">
                <div className="self-stretch rounded-radius-md overflow-hidden flex flex-row items-start justify-start pt-2 shadow-[0px_1px_2px_rgba(16,_24,_40,_0.05)]">
                  <InputsNew
                    label="Enhancement Details*"
                    type="textarea"
                    register={editRegister("enhancement_details")}
                    errorMessage={editErrors.enhancement_details}
                    contentPlaceholder="Describe the enhancement in detail..."
                  />
                </div>
              </div>

              {/* Remarks */}
              <div className="self-stretch flex flex-col items-start justify-start text-sm">
                <div className="self-stretch rounded-radius-md overflow-hidden flex flex-row items-start justify-start pt-2 shadow-[0px_1px_2px_rgba(16,_24,_40,_0.05)]">
                  <InputsNew
                    label="Remarks"
                    type="textarea"
                    register={editRegister("remarks")}
                    errorMessage={editErrors.remarks}
                    contentPlaceholder="Any additional remarks..."
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="self-stretch flex flex-row items-center justify-end py-0 pr-0 gap-[8px] mq950:box-border mq1425:flex-wrap mq1425:box-border mq450:box-border">
                <button
                  className="cursor-pointer bg-white py-2.5 h-[44px] mq575:h-[35px] px-[42px] btn-text-purple mq575:py-[12.5px] mq575:px-[25px] rounded-radiuscomponent-button box-border flex flex-row items-center justify-center gap-[12px] border-[2px] border-solid border-surface-primary-surface hover:bg-gainsboro-100"
                  type="button"
                  onClick={() => navigate(-1)}
                >
                  <div className="relative text-sm font-body-xs-semi-bold  dark:text-white text-center inline-block">
                    Cancel
                  </div>
                </button>
                <button
                  className="cursor-pointer [border:none] py-2.5 h-[44px] mq575:h-[35px] px-[42px] mq575:py-[12.5px] mq575:px-[25px] bg-surface-primary-surface  flex flex-row items-center justify-center gap-[12px] hover:bg-dodgerblue"
                  type="submit"
                >
                  <div className="relative text-sm font-body-xs-semi-bold text-surface-bg-primary text-center inline-block">
                    Update
                  </div>
                </button>
              </div>
            </form>
          )}

          {/* ── HISTORY TAB ───────────────────────────────────── */}
          {activeTab === "history" && (
            <div className="self-stretch flex flex-col gap-6">
              {/* Add history entry form */}
              <form
                onSubmit={handleHistSubmit(onHistorySubmit)}
                className="m-0 self-stretch rounded-radius-radius-lg bg-surface-bg-blue darkborder-b-remove flex flex-col items-start justify-start p-8 mq575:p-3 box-border gap-spacing-lg max-w-full"
              >
                <h4 className="m-0 text-base font-bold dark:text-white">
                  Add Remark
                </h4>

                <div className="grid grid-cols-4 mq1400:grid-cols-4 mq1275:grid-cols-3 mq950:grid-cols-2 mq575:grid-cols-1 gap-[8px] min-w-full">
                  <Controller
                    name="worked_on"
                    control={histControl}
                    render={({ field }) => (
                      <DateSelector
                        label="Worked On*"
                        selectedDate={field.value}
                        onChange={(date: any) => {
                          field.onChange(date);
                        }}
                        nothideParsedValue={true}
                        blueIcon={true}
                      />
                    )}
                  />
                </div>

                <div className="self-stretch flex flex-col items-start justify-start text-sm">
                  <div className="self-stretch rounded-radius-md overflow-hidden flex flex-row items-start justify-start pt-2 shadow-[0px_1px_2px_rgba(16,_24,_40,_0.05)]">
                    <InputsNew
                      label="Changes Made*"
                      type="textarea"
                      register={histRegister("changes_made")}
                      errorMessage={histErrors.changes_made}
                      contentPlaceholder="Describe what was changed or investigated..."
                    />
                  </div>
                </div>

                <div className="self-stretch flex flex-row items-center justify-end gap-[8px]">
                  <button
                    className="cursor-pointer [border:none] py-2.5 h-[44px] mq575:h-[35px] px-[42px] mq575:py-[12.5px] mq575:px-[25px] bg-surface-primary-surface rounded-radiuscomponent-button flex flex-row items-center justify-center gap-[12px] hover:bg-dodgerblue"
                    type="submit"
                  >
                    <div className="relative text-sm font-body-xs-semi-bold text-surface-bg-primary text-center inline-block">
                      Add Entry
                    </div>
                  </button>
                </div>
              </form>

              {/* History timeline */}
              <div className="m-0 self-stretch rounded-radius-radius-lg bg-surface-bg-blue darkborder-b-remove flex flex-col items-start justify-start p-8 mq575:p-3 box-border gap-4 max-w-full">
                <h4 className="m-0 text-base font-bold dark:text-white">
                  Activity History
                  <span className="ml-2 text-sm font-normal text-gray-500">
                    ({historyList.length}{" "}
                    {historyList.length === 1 ? "entry" : "entries"})
                  </span>
                </h4>

                {historyList.length === 0 ? (
                  <div className="self-stretch flex items-center justify-center py-10 text-sm text-gray-400 dark:text-gray-500">
                    No history entries yet.
                  </div>
                ) : (
                  <div className="self-stretch flex flex-col gap-3">
                    {[...historyList]
                      .reverse()
                      .map((entry: any, idx: number) => (
                        <div
                          key={entry.id ?? idx}
                          className="self-stretch rounded-lg bg-white dark:bg-slate-700 border border-surface-border p-4 flex flex-col gap-3"
                        >
                          {/* Header: avatar + name + dates */}
                          <div className="flex flex-row items-start justify-between gap-2 flex-wrap">
                            <div className="flex items-center gap-2">
                              <span className="w-8 h-8 rounded-full bg-surface-primary-surface text-white text-xs flex items-center justify-center font-bold shrink-0">
                                {entry.worked_by_name
                                  ?.charAt(0)
                                  ?.toUpperCase() ?? "?"}
                              </span>
                              <div className="flex flex-col">
                                <span className="text-sm font-semibold dark:text-white">
                                  {entry.worked_by_name ?? "—"}
                                </span>
                                <span className="text-xs text-gray-400">
                                  Worked on:{" "}
                                  {entry.worked_on
                                    ? format(
                                        new Date(entry.worked_on),
                                        "MMM dd, yyyy",
                                      )
                                    : "—"}
                                </span>
                              </div>
                            </div>
                            <span className="text-xs text-gray-400 dark:text-gray-400 mt-1">
                              Logged:{" "}
                              {entry.created_on
                                ? format(
                                    new Date(entry.created_on),
                                    "MMM dd, yyyy",
                                  )
                                : "—"}
                            </span>
                          </div>

                          {/* Changes description */}
                          <div className="pl-10">
                            <p className="m-0 text-sm text-gray-700 dark:text-gray-200 leading-relaxed">
                              {entry.changes_made}
                            </p>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {isLoading && <Loader />}
    </>
  );
};

export default ViewEnhancement;
